import { useCreditDialogTracking } from "@/components/features/Credits/useCreditDialogTracking";
import { Dialog } from "@/design-system/Dialog/Dialog";
import { trackClubImportCta } from "@/events/clubImportMixpanel";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import type { ClubImportOrigin } from "@repo/shared/redux";
import { type GatedFeatureId, featureActionPhrase } from "@repo/shared/redux";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

export type ClubImportGateData = {
  /** The feature the user was trying to reach; names the action in the copy. */
  featureId?: GatedFeatureId;
  /** The surface the gate was raised from, for the CTA event. */
  origin?: ClubImportOrigin;
};

/**
 * Shown when a club feature is used before a club exists. A prerequisite gate,
 * not a paywall — deliberately no upgrade pitch.
 *
 * Neither button records a signup intent: promptClubImport already did, when it
 * raised this dialog. Doing it again here would be the same value written twice,
 * and doing it *only* here would miss the user who reaches login through the
 * "Log in" link rather than the import CTA.
 */
export const ClubImportGateDialog = ({
  data,
  onClose,
}: {
  data?: ClubImportGateData;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["clubImport", "common"]);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const action = data?.featureId
    ? featureActionPhrase(data.featureId)
    : t("gate.defaultAction");
  const { trackExit } = useCreditDialogTracking({
    context: "club-import-gate",
    featureId: data?.featureId,
  });

  return (
    <Dialog
      isOpen
      onClose={() => {
        trackExit("negative");
        onClose();
      }}
      title={t("gate.title")}
      width={380}
      negLabel={t("gate.maybeLater")}
      posLabel={t("gate.cta")}
      onNegative={() => trackExit("negative")}
      onPositive={() => {
        trackExit("positive");
        // Reported against the surface the gate was raised from, not the gate:
        // "Squad Builder" is the answer to where this import came from.
        trackClubImportCta(
          data?.origin ?? { section: "Credits", subsection: data?.featureId },
        );
        navigate("/club-import-tutorial");
      }}
    >
      <p>{t("gate.body", { action })}</p>
      {!isLoggedIn && (
        <p className="mt-3 text-xs">
          {t("gate.alreadyImported")}{" "}
          <button
            className="text-dark-secondary underline underline-offset-2"
            onClick={() => {
              // The link is the "already imported" answer to the same offer, so
              // it counts with the CTA rather than as its own vocabulary.
              trackExit("positive");
              onClose();
              dispatch(showModal({ modalType: "login-required" }));
            }}
          >
            {t("common:logIn")}
          </button>
        </p>
      )}
    </Dialog>
  );
};
